import type React from 'react';

import { Box, FormField, FormHelp, Input, Label, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PROPERTY_ADDRESS } from '../../../constants/index';
import { isMultiFamily } from './Utilities/MultiFamilyLandlordUtility';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function PropertyAddress_v2({ section, setSectionValues }: Props): JSX.Element {
  function onPropertyAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS]: value,
    });
  }

  function onUnitNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.UNIT_NUMBER]: value,
    });
  }

  function onComplexAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS]: value,
    });
  }

  function onYesClicked(): void {
    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PART_OF_COMPLEX]: true,
    });
  }

  function onNoClicked(): void {
    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PART_OF_COMPLEX]: false,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        className={FULLSTORY_EXCLUDE}
        error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
        label={
          <Label id="property-address">
            {isMultiFamilyLandlord ? 'What is the street address of the property?' : 'What is the property’s address?'}
          </Label>
        }
        control={
          <Textarea
            aria-labelledby="property-address"
            css={{ resize: 'vertical', height: '7.125rem', '&:focus': { height: '7.125rem' } }}
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
            onChange={onPropertyAddressChange}
          />
        }
        description={
          section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS) ? (
            <FormHelp>{PROPERTY_ADDRESS.ERROR_MESSAGE.PROPERTY_ADDRESS}</FormHelp>
          ) : (
            <FormHelp>Street, city, state, ZIP code</FormHelp>
          )
        }
      />
      {isMultiFamilyLandlord && (
        <>
          <FormField
            optional
            css={{ marginBottom: 'layout.tight' }}
            className={FULLSTORY_EXCLUDE}
            label={
              <Label id="unit-number" optional>
                What is the unit number?
              </Label>
            }
            control={
              <Input
                aria-labelledby="unit-number"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.UNIT_NUMBER)}
                onChange={onUnitNumberChange}
              />
            }
          />
          <Label>Is the property or unit part of a complex with a different address?</Label>
          <Box css={{ marginTop: 'layout.tighter' }} />
          <YesAndNoExpansion
            yesHandler={onYesClicked}
            noHandler={onNoClicked}
            yesActive={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.PART_OF_COMPLEX)}
          >
            <FormField
              required
              css={{ marginTop: 'lg' }}
              className={FULLSTORY_EXCLUDE}
              error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS)}
              label={<Label id="complex-address">Address of the complex</Label>}
              control={
                <Textarea
                  aria-labelledby="complex-address"
                  css={{ resize: 'vertical', height: '8.625rem', '&:focus': { height: '8.625rem' } }}
                  value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS)}
                  onChange={onComplexAddressChange}
                />
              }
              description={
                section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS) ? (
                  <FormHelp>{PROPERTY_ADDRESS.ERROR_MESSAGE.COMPLEX_ADDRESS}</FormHelp>
                ) : (
                  <FormHelp>Name of complex, street, city, state, ZIP code</FormHelp>
                )
              }
            />
          </YesAndNoExpansion>
        </>
      )}
    </>
  );
}
